import type { GlossaryEntry } from '@aica/core';
import { escapeXmlAttr, escapeXmlText } from './escape';

/**
 * Render ONE client-glossary entry as XML.
 *
 * Two prompts describe the same `GlossaryEntry` to the model: the analyzer's
 * user prompt (`<client_glossary>` → `<entry>`) and the categorizer's client
 * context (`<glossary_entry>`). They were written twice — same four fields,
 * same `|` alias join, same escape helpers — differing only in tag name and
 * indentation. Two renderers for one type means adding a field to
 * `GlossaryEntrySchema` (packages/core/src/schemas/analysis-client-config.ts),
 * or changing the alias delimiter, silently updates one prompt and not the
 * other: the categorizer and the analyzer then describe the same client
 * vocabulary to the model in two different vocabularies.
 *
 * The tag name and indentation stay parameters because both prompts are
 * cache-sensitive text — this extraction must not move a single byte of
 * either one.
 */
export function renderGlossaryEntry(
  entry: GlossaryEntry,
  options: { tagName: string; indent: string }
): string {
  const { tagName, indent } = options;
  const aliases = entry.aliases?.length
    ? ` aliases="${escapeXmlAttr(entry.aliases.join('|'))}"`
    : '';
  const note = entry.note ? escapeXmlText(entry.note) : '';
  return `${indent}<${tagName} term="${escapeXmlAttr(entry.term)}" type="${escapeXmlAttr(
    entry.type
  )}"${aliases}>${note}</${tagName}>`;
}
